// Ch78 · ONE filter bar for the whole Leads page.
//
// Grant (2026-09-10): "this whole app UI/UX still feels messy and too much
// overlap of things."
//
// He was looking at the symptom: the page used to carry TWO filter sets. The
// calendar at the top showed everybody; the list at the bottom had its own
// chips and its own calendar view showing a different, narrower set. Two
// pictures of two populations, neither labelled — so "Real only" narrowed the
// table while the calendar above it kept drawing everyone, and the counts in
// between belonged to neither.
//
// One bar, one population. Tick "Real only" and the calendar, the work list and
// every count on the page agree, because they are all reading the same rows.
// Filtering is a question about WHO, and a page should only be able to answer
// that once.
//
// What this bar deliberately does NOT touch: the analytics band. Its range is a
// question about WHEN people arrived, it governs only the funnel and two cards,
// and it lives down there with them.

import React from "react";
import { Box, Chip, Paper, Stack, TextField, Typography } from "@mui/material";

export interface LeadsFilters {
  realOnly: boolean;
  textsOk: boolean;
  next30: boolean;
}

export type LeadsFilterKey = keyof LeadsFilters;

export interface LeadsFilterBarProps {
  filters: LeadsFilters;
  onToggle: (key: LeadsFilterKey) => void;
  search: string;
  onSearch: (value: string) => void;
  /** How many people survive the filter, for the "showing N" readout. */
  shown: number;
  /** How many there are in total, so a filter's cost is visible. */
  total: number;
  /**
   * Who the current search matches, for the jump-to list. Empty while the box
   * is empty — a dropdown that appears before you type is a menu, not an answer.
   */
  matches?: SearchMatch[];
  /** Take me to this person: the calendar jumps to their move-in month. */
  onPick?: (key: string) => void;
}

export interface SearchMatch {
  key: string;
  name: string;
  /** Their move-in day in words, or null when they never gave one. */
  moveInLabel: string | null;
  /** Null means there is no day to jump to — say so instead of jumping nowhere. */
  dateMs: number | null;
}

const CHIPS: ReadonlyArray<{ key: LeadsFilterKey; label: string }> = [
  { key: "realOnly", label: "Real only" },
  { key: "textsOk", label: "Texts OK" },
  { key: "next30", label: "Next 30 days" },
];

export default function LeadsFilterBar({
  filters,
  onToggle,
  search,
  onSearch,
  shown,
  total,
  matches = [],
  onPick,
}: LeadsFilterBarProps) {
  const narrowed = shown !== total;
  return (
    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }} data-testid="leads-filter-bar">
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        {CHIPS.map((c) => (
          <Chip
            key={c.key}
            size="small"
            label={c.label}
            data-testid={`leads-filter-${c.key}`}
            onClick={() => onToggle(c.key)}
            variant={filters[c.key] ? "filled" : "outlined"}
            color={filters[c.key] ? "primary" : "default"}
          />
        ))}
        <TextField
          size="small"
          placeholder="Search leads…"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          inputProps={{ "data-testid": "leads-search", "aria-label": "Search leads" }}
          sx={{ flex: 1, minWidth: 200 }}
        />
        {/* The cost of the filter, stated. A narrowed page that does not say so
            is how "we have no leads" gets believed. */}
        <span data-testid="leads-filter-count" style={{ fontSize: 12, opacity: 0.7, whiteSpace: "nowrap" }}>
          {narrowed ? `showing ${shown} of ${total}` : `${total} people`}
        </span>
      </Stack>

      {/* Jump-to. Typing narrows the page; picking a name takes you to the day
          they move. "When is X moving?" is a question that used to need a
          search, then a scroll, then a month of paging. */}
      {search.trim() !== "" && matches.length > 0 && onPick && (
        <Stack spacing={0.25} sx={{ mt: 1 }} data-testid="leads-search-matches">
          {matches.map((m) => (
            <Box
              key={m.key}
              component="button"
              onClick={() => onPick(m.key)}
              data-testid={`leads-search-pick-${m.key}`}
              sx={{
                display: "flex",
                alignItems: "baseline",
                gap: 1,
                width: "100%",
                px: 1,
                py: 0.5,
                border: "none",
                borderRadius: 1,
                cursor: "pointer",
                bgcolor: "transparent",
                fontFamily: "inherit",
                textAlign: "left",
                color: "text.primary",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <Typography variant="body2" fontWeight={700}>{m.name}</Typography>
              <Typography variant="caption" color={m.dateMs === null ? "text.disabled" : "text.secondary"}>
                {m.dateMs === null
                  ? m.moveInLabel
                    // Their WORDS, quoted, so "August" cannot be mistaken for a
                    // day the calendar could page to. The verb on the right says
                    // what will actually happen.
                    ? `“${m.moveInLabel}” · not a date`
                    : "no move-in date"
                  : m.moveInLabel}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Typography variant="caption" color="primary.main" fontWeight={700}>
                {m.dateMs === null ? "open" : "jump to day"}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </Paper>
  );
}
